import { Company, Indicator, PopulatedValue } from "@ecountabl/lib"
import { Formik } from "formik"
import React, { useContext, useMemo, useRef, useState } from "react"
import { useAppSelector } from "../store"
import * as yup from 'yup'
import { Animated, Keyboard, Text, View, StyleSheet } from "react-native"
import { CustomActionSheetScrollView } from "../utility/CustomActionSheet"
import { ThemedButton } from "../utility/input/buttons"
import { textStyles } from "../style"
import { ThemedTextInput } from "../utility/input/textinputs/themed-text-input"
import { ThemedDropdown } from "../utility/input/misc/themed-dropdown"
import api from "../api-client"
import { ThemeContext } from "../utility/context/ThemeContext"
import SuccessAnimation from "../utility/success-animation"
import ErrorAnimation from "../utility/error-animation"
import Analytics from "../utility/analytics"

enum CompanyInfoFeedbackType {
  AlternativeCompany = "Alternative Company",
  Category = "Category",
  Certification = "Certification",
  Headquarters = "Headquarters",
  Logo = "Logo",
  Name = "Name",
  ParentAffiliation = "Parent Affiliation",
  Segment = "Segment",
  Values = "Values",
  Website = "Website",
  Other = "Other"
}

type Props = {
  company: Company
  closeSheet: () => void
}

interface FormValues {
  category: string;
  userFreeFormInput: string;
  alternativeCompanyId?: string;
}

enum SubmitStep {
  NotSubmitted,
  Submitting,
  Submitted,
  InputError,
  ServerError
}

const validationSchema = yup.object().shape({
  category: yup.string().required().label("Category"),
  userFreeFormInput: yup.string().required().label("Explanation"),
  alternativeCompanyId: yup.string().when('category', {
    is: CompanyInfoFeedbackType.AlternativeCompany,
    then: yup.string().required().typeError("Please select an alternative company"),
    otherwise: yup.string().notRequired()
  }),
})

const UserCompanyFeedbackForm = ({company, closeSheet}: Props) => {
  const { map } = useAppSelector(state => state.values)
  const [submitStep, setSubmitStep] = useState(SubmitStep.NotSubmitted)
  const [inputErrors, setInputErrors] = useState<string[]>([])
  const suggestionRowHeight = useRef(new Animated.Value(1)).current
  const maxRowHeight = suggestionRowHeight.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 100] // needs to be > content height
  })

  const { textMain } = useContext(ThemeContext).theme.colors

  const allSuggestedCompanies = useMemo(() => {
    const suggestedCompanies = {}

    for (const tag of Object.keys(company.altsByTag || {})) {
      for (const suggestedCompany of company.altsByTag[tag]) {
        if (suggestedCompany._id === company._id) continue

        suggestedCompanies[suggestedCompany._id] = {
          name: suggestedCompany.name,
          tag: tag
        }
      }
    }

    return Object.keys(suggestedCompanies).map(_id => ({
      _id,
      name: suggestedCompanies[_id].name,
      tag: suggestedCompanies[_id].tag
    })).sort((a, b) => a.name.localeCompare(b.name))
  }, [company])

  const companyCerts = useMemo(() => {
    const certs = []
    const isCert = (i: Indicator) => (
      i.certification_value &&
      company.calculatedRatings[i.abbreviation] == i.certification_value
    )

    Object.values(map).forEach((v:PopulatedValue) => {
      certs.push(...v.indicators.filter(isCert))
    })

    return certs
  }, [company])

  const categoryOptions = useMemo(() => {
    const cats = {} as Record<CompanyInfoFeedbackType, string>

    cats[CompanyInfoFeedbackType.Category] = 'Category is incorrect'
    cats[CompanyInfoFeedbackType.Certification] = `Certification is ${companyCerts.length === 0 ? 'missing' : 'incorrect or missing'}`
    cats[CompanyInfoFeedbackType.Headquarters] = 'Headquarters is incorrect or missing'
    cats[CompanyInfoFeedbackType.Logo] = 'Logo is incorrect or missing'
    cats[CompanyInfoFeedbackType.Name] = 'Name is incorrect'
    cats[CompanyInfoFeedbackType.ParentAffiliation] = `Corporate parent affiliation is ${company.brands ? 'incorrect or missing' : 'missing'}`
    cats[CompanyInfoFeedbackType.Segment] = 'Segment is incorrect'
    cats[CompanyInfoFeedbackType.Values] = 'Values are not accurate'
    cats[CompanyInfoFeedbackType.Website] = `Website is ${company.website ? 'not active or is incorrect' : 'missing'}`
    cats[CompanyInfoFeedbackType.Other] = 'Other'

    // if there are alternatives and we are not hiding them, users can give feedback on them
    if (allSuggestedCompanies.length) {
      cats[CompanyInfoFeedbackType.AlternativeCompany] = 'Alternative Company is incorrect'
    }

    return (
      Object.keys(cats).map(key => {
      return {label: cats[key], value: key}
      }).sort((a, b) => {
        if (a.label === 'Other') return 1
        if (b.label === 'Other') return -1
        return a.label.localeCompare(b.label)
      })
    )
  }, [company, allSuggestedCompanies, companyCerts])
    

  return (
    <Formik<FormValues>
      initialValues={{
        category: undefined,
        userFreeFormInput: "",
      }}
      onSubmit={(formValues) => {
        setSubmitStep(SubmitStep.Submitting)
        
        const isAltComapnyFeedback = (
          formValues.category === CompanyInfoFeedbackType.AlternativeCompany
        )

        Analytics.track('user_company_info_feedback', {
          category: formValues.category,
          companyId: company._id,
          companyName: company.name,
          alternativeCompanyId: isAltComapnyFeedback ? formValues.alternativeCompanyId : undefined,
          alternativeCompanyName: isAltComapnyFeedback
            ? allSuggestedCompanies.find(c => c._id === formValues.alternativeCompanyId)?.name
            : undefined,
          has_freeform: !!formValues.userFreeFormInput,
        })

        api.submitCompanyInfoFeedback({
          category: formValues.category as keyof typeof CompanyInfoFeedbackType,
          userFreeFormInput: formValues.userFreeFormInput,
          alternativeCompanyId: isAltComapnyFeedback ?
            formValues.alternativeCompanyId
            : undefined,
          alternativeCompanyName: isAltComapnyFeedback ?
            allSuggestedCompanies.find(c => c._id === formValues.alternativeCompanyId).name
            : undefined,
          companyId: company._id,
          companyName: company.name,
        }).then(() => {
          setSubmitStep(SubmitStep.Submitted)
        }).catch((err) => {
          if (err.status == 400) {
            setSubmitStep(SubmitStep.InputError)
            setInputErrors(err.errors)
          } else {
            setSubmitStep(SubmitStep.ServerError)
          }
        })
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({
        handleChange, values, errors, touched, handleSubmit, setFieldValue, initialValues
      }) => {
        const hasValues = Object.keys(values).some(key => !!values[key] === true)
        const displayAltCompanyField = (
          values.category && 
          values.category === CompanyInfoFeedbackType.AlternativeCompany &&
          allSuggestedCompanies.length > 0
        )

        if (submitStep === SubmitStep.Submitted) {
          return <>
            <CustomActionSheetScrollView bounces={false}>
              <View style={{gap: 8}}>
                <Text style={[textStyles.h2, {marginBottom: 10, color: textMain}]}>
                  Suggest Company Info Changes
                </Text>
                <View style={{alignItems: "center", gap: 8}}>
                  <SuccessAnimation size={250} />
                  <Text style={[textStyles.h2, {color: textMain}]}>
                    Success!
                  </Text>
                  <Text style={[textStyles.body, {color: textMain, textAlign: 'center'}]}>
                    Thanks for the feedback! We&apos;ll be sure to check it out as soon as possible.{"\n"}
                  </Text>
                  <Text style={[textStyles.body, {color: textMain, textAlign: 'center'}]}>
                    Feel free to check back soon to see if we&apos;ve made any changes.
                  </Text>
                </View>
              </View>
            </CustomActionSheetScrollView>
            <View style={styles.submitButtonWrapper}>
              <ThemedButton
                text="Close"
                onPress={closeSheet}
                variant="primary"
              />
            </View>
          </>
        } else if (submitStep === SubmitStep.ServerError) {
          return <>
            <CustomActionSheetScrollView bounces={false}>
              <View style={{gap: 8}}>
                <Text style={[textStyles.h2, {marginBottom: 10, color: textMain}]}>
                  Suggest Company Info Changes
                </Text>
                <View style={{alignItems: "center", gap: 8}}>
                  <ErrorAnimation size={250} />
                  <Text style={[textStyles.h2, {color: textMain}]}>
                    Error!
                  </Text>
                  <Text style={[textStyles.body, {marginBottom: 10, color: textMain}]}>
                    Uh oh, there was an error submitting your feedback.
                  </Text>
                  <Text style={[textStyles.body, {marginBottom: 10, color: textMain}]}>
                    We aim to provide accurate data and want to hear from{' '}
                    you so please try again later.
                  </Text>
                </View>
              </View>
              
            </CustomActionSheetScrollView>
            <View style={styles.submitButtonWrapper}>
              <ThemedButton
                text="Close"
                onPress={closeSheet}
                variant="primary"
              />
            </View>
          </>
        }
        return <>
          <CustomActionSheetScrollView>
            <View style={{gap: 8}}>
              <Text style={[textStyles.h2, {color: textMain}]}>
                Suggest Company Info Changes
              </Text>
              <Text style={[textStyles.body, {color: textMain}]}>
                We appreciate your help in ensuring the accuracy of our data.{' '}
                Your suggestions will be sent to our team of moderators for review.
              </Text>
            </View>
            <View style={{gap: 8}}>
              <ThemedDropdown
                label="Category"
                required
                data={categoryOptions}
                value={values.category}
                onChange={(val) => {
                  if (val === CompanyInfoFeedbackType.AlternativeCompany) {
                    setFieldValue('alternativeCompanyId', null)
                  }
                  handleChange('category')(val)
                }}
                displayTextField="label"
                valueField="value"
                errorText={touched.category ? errors.category : undefined}
                disabled={submitStep === SubmitStep.Submitting}
                placeholder="Select a category"
              />
              <Animated.View style={{maxHeight: maxRowHeight, overflow: "hidden"}}>
                {
                  displayAltCompanyField ? 
                  <ThemedDropdown
                    label="Alternative Company"
                    required
                    data={allSuggestedCompanies}
                    value={values.alternativeCompanyId}
                    onChange={(val) => handleChange('alternativeCompanyId')(val)}
                    displayTextField="name"
                    valueField="_id"
                    errorText={
                      touched.alternativeCompanyId ? errors.alternativeCompanyId : undefined
                    }
                    disabled={submitStep === SubmitStep.Submitting}
                    placeholder="Select an alternative company"
                  /> : null
                }
              </Animated.View>
              <ThemedTextInput
                value={values.userFreeFormInput}
                onChange={(text) => {
                  if (text === "\n") {
                    Keyboard.dismiss()
                    return
                  }
                  handleChange('userFreeFormInput')(text)
                }}
                label="Explanation"
                placeholder={`${company.name} has inaccurate information because ...`}
                required
                textInputProps={{
                  autoComplete: "off",
                  autoCorrect: false,
                  multiline: true,
                  numberOfLines: 4,
                  returnKeyType: "done",
                  style: {
                    maxHeight: 200,
                    minHeight: 100
                  },
                }}
                disabled={submitStep === SubmitStep.Submitting}
                errorText={touched.userFreeFormInput ? errors.userFreeFormInput : undefined}
              />
            </View>
            {
            submitStep === SubmitStep.InputError && inputErrors.length > 0 ? 
            <View style={{paddingTop: 10}}>
              {
                inputErrors.map((err, i) => (
                  <Text key={i} style={[textStyles.subscript, {color: "red"}]}>{err}</Text>
                ))
              }
            </View> : null
            }
          </CustomActionSheetScrollView>

          <View style={styles.submitButtonWrapper}>
            <ThemedButton
              text={hasValues ? "Submit Feedback" : "Close"}
              onPress={hasValues ? handleSubmit : closeSheet}
              loading={submitStep === SubmitStep.Submitting}
              variant="primary"
            />
          </View>
        </>
      }}
    </Formik>
  )
}

export default UserCompanyFeedbackForm

const styles = StyleSheet.create({
  submitButtonWrapper: {
    paddingHorizontal: 16,
    paddingTop: 16
  }
})